import { useEffect, useState } from "react";
import { useStoryThumbnail } from "../hooks/useStoryThumbnail";

interface StoryThumbnailProps {
    storyId: string;
    size?: "sm" | "md" | "lg";
    className?: string;
    alt?: string;
    onClick?: () => void;
}

const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
};

export function StoryThumbnail({
    storyId,
    size = "md",
    className = "",
    alt = "Story thumbnail",
    onClick,
}: StoryThumbnailProps) {
    const { data: thumbnailBlob, isLoading, error } = useStoryThumbnail(storyId);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

    const baseClasses = `${sizeClasses[size]} bg-muted rounded overflow-hidden ${className}`;
    const containerClasses = onClick
        ? `${baseClasses} cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all`
        : baseClasses;

    const containerProps = onClick
        ? {
              onClick,
              role: "button",
              tabIndex: 0,
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onClick();
                  }
              },
          }
        : {};

    // Convert blob to object URL when data is available
    useEffect(() => {
        if (thumbnailBlob) {
            const url = URL.createObjectURL(thumbnailBlob);
            setThumbnailUrl(url);
            setImageLoaded(false); // Reset image loaded state for new blob
        }
    }, [thumbnailBlob]);

    // Cleanup object URL when component unmounts or URL changes
    useEffect(() => {
        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailUrl]);

    return (
        <div className={containerClasses} {...containerProps}>
            {(isLoading || !thumbnailUrl) && <div className="w-full h-full bg-muted animate-pulse" />}
            {thumbnailUrl && !error && (
                <img
                    src={thumbnailUrl}
                    alt={alt}
                    className={`w-full h-full object-cover ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                    onLoad={() => setImageLoaded(true)}
                    loading="lazy"
                />
            )}
            {error && !thumbnailUrl && (
                <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground text-xs">
                    Failed to load
                </div>
            )}
        </div>
    );
}
